import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { POSITIONS } from '../../../shared/constants';
import { IMdBody } from '../../../types/shared';

import { validateForm } from '../../../utils/forms/formValidation';
import { formStringRegex } from '../../../utils/forms/regexHelpers';
import { FORM_LABEL } from '../../../utils/forms/translationKeys';
import ReactMarkdownExtended from '../../../utils/generic/ReactMarkdownExtended';
import { ITranslationItem, languageHelper } from '../../../utils/language';

import Colors from '../../../styles/colors';
import {
  ButtonLabel,
  Label,
  LabelLight,
  LabelWrapper,
  SmallText,
} from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import ErrorMessage from '../../molecules/error-message/ErrorMessage';
import InfoPopover from '../info-popover/InfoPopover';

interface FormInputProps {
  id: string;
  type?: string;
  name: string;
  value?: string;
  onChange?: (event?: React.FormEvent<HTMLInputElement>) => void;
  onInvalidHandler?: (
    validity: ValidityState,
    event?: React.FormEvent<HTMLInputElement>,
    customError?: string
  ) => void;
  placeholder?: string;
  required?: boolean;
  label: string;
  error?: string;
  tooltip?: IMdBody | null;
  helperMessage?: string;
  tooltipOrientation?: string;
  className?: string;
  context?: string;
}

const FormInput = ({
  id,
  type = 'text',
  name,
  value,
  onChange,
  onInvalidHandler,
  placeholder,
  required,
  label,
  error,
  tooltip,
  helperMessage,
  tooltipOrientation = POSITIONS.left,
  className,
  context,
}: FormInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<ITranslationItem>();
  const { lang } = languageHelper();

  const validateFormInput = (
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLInputElement>
      | React.FormEvent<HTMLInputElement>
  ) => {
    if (!inputRef?.current) {
      return;
    }

    const { validity } = inputRef.current;

    if (validity.valid && onChange) {
      onChange(event);
    }

    if (onInvalidHandler) {
      onInvalidHandler(validity, event, label);
    } else {
      setErrorMessage(
        validateForm(
          validity,
          label,
          undefined,
          2,
          50,
          type,
          undefined,
          context
        )
      );
    }
  };

  return (
    <Label className={className} data-testid="form-input" htmlFor={id}>
      <LabelWrapper>
        {label}
        {!required && (
          <StyledLabelLight>{` ${FORM_LABEL.required[lang]}`}</StyledLabelLight>
        )}
        {tooltip && (
          <InfoPopover infoDirection={tooltipOrientation} label={label}>
            <ReactMarkdownExtended>
              {tooltip.childMarkdownRemark?.rawMarkdownBody}
            </ReactMarkdownExtended>
          </InfoPopover>
        )}
      </LabelWrapper>
      <StyledInput
        data-testid="input"
        data-pw-testid={`input-${type}-${id}`}
        error={error || (errorMessage && errorMessage[lang])}
        aria-live="assertive"
        id={id}
        type={type}
        name={name}
        pattern={formStringRegex(type, context)}
        defaultValue={value}
        placeholder={placeholder}
        required={required}
        onChange={validateFormInput}
        onInvalid={(e) => {
          e.preventDefault();
          validateFormInput();
        }}
        ref={inputRef}
      />
      {helperMessage && <StyledSmallText>{helperMessage}</StyledSmallText>}
      {errorMessage && <ErrorMessage>{errorMessage[lang]}</ErrorMessage>}
    </Label>
  );
};

export default FormInput;

const StyledInput = styled.input<{ error?: unknown }>`
  ${ButtonLabel}
  border: 1px solid
    ${({ error }) => (error ? Colors.abnNegative400 : Colors.abnNeutrals75)};
  border-radius: ${({ theme }) => theme.radiuses.S};
  box-sizing: border-box;
  font-family: ${({ theme }) => theme.fontRegular};
  height: 40px;
  margin-top: 8px;
  padding: 8px;
  width: 100%;
`;

const StyledSmallText = styled(SmallText)`
  color: ${Colors.abnNeutral400};
  margin-top: ${Spacings.XS};
`;

const StyledLabelLight = styled(LabelLight)`
  color: ${({ theme }) => theme.colors.abnNeutral300};
  font-family: ${({ theme }) => theme.fontRegular};
`;
